import { auth } from './firebaseClient';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebaseClient';

/**
 * Auth Helper - Adaptador de autenticación para reemplazar Base44 Auth
 * 
 * Proporciona una API similar a base44.auth usando Firebase Authentication
 */

class AuthHelper {
  constructor() {
    this.currentUser = null;
    this.listeners = [];
    
    // Escuchar cambios en el estado de autenticación
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Obtener datos adicionales del usuario desde Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          this.currentUser = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            emailVerified: user.emailVerified,
            ...(userDoc.exists() ? userDoc.data() : {})
          };
        } catch (error) {
          console.error('Error fetching user data:', error);
          this.currentUser = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            emailVerified: user.emailVerified,
          };
        }
      } else {
        this.currentUser = null;
      }
      
      // Notificar a los listeners
      this.listeners.forEach(listener => listener(this.currentUser));
    });
  }

  /**
   * Obtener el usuario actual
   * Similar a base44.auth.me()
   * @returns {Promise<Object>} Usuario actual
   */
  async me() {
    if (!this.currentUser) {
      throw new Error('No authenticated user');
    }
    return this.currentUser;
  }

  /**
   * Iniciar sesión con email y contraseña
   * @param {string} email 
   * @param {string} password 
   * @returns {Promise<Object>} Usuario autenticado
   */
  async signIn(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Obtener datos adicionales del usuario
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      
      return {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName,
        photoURL: userCredential.user.photoURL,
        emailVerified: userCredential.user.emailVerified,
        ...(userDoc.exists() ? userDoc.data() : {})
      };
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  }

  /**
   * Cerrar sesión
   * @returns {Promise<void>}
   */
  async signOut() {
    try {
      await signOut(auth);
      this.currentUser = null;
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  /**
   * Enviar email de restablecimiento de contraseña
   * @param {string} email 
   * @returns {Promise<void>}
   */
  async sendPasswordReset(email) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error sending password reset:', error);
      throw error;
    }
  }

  /**
   * Actualizar perfil del usuario
   * @param {Object} data - { displayName, photoURL }
   * @returns {Promise<void>}
   */
  async updateUserProfile(data) {
    try {
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, data);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  /**
   * Suscribirse a cambios en el estado de autenticación
   * @param {Function} callback 
   * @returns {Function} Función para desuscribirse
   */
  onAuthStateChange(callback) {
    this.listeners.push(callback);
    
    // Llamar inmediatamente con el estado actual
    if (this.currentUser) {
      callback(this.currentUser);
    }
    
    // Retornar función para desuscribirse
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  /**
   * Verificar si hay un usuario autenticado
   * @returns {boolean}
   */
  isAuthenticated() {
    return this.currentUser !== null;
  }

  /**
   * Obtener el token de autenticación actual
   * @returns {Promise<string>}
   */
  async getToken() {
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken();
    }
    throw new Error('No authenticated user');
  }
}

// Exportar instancia singleton
export const authHelper = new AuthHelper();

export default authHelper;
